import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { milliseconds } from 'date-fns';

import {
  type EligibilityInput,
  type InsuranceEligibilityVerificationResponse,
  insuranceV3Verification,
} from '@/api/generated';
import { getDebugVerifiedOverride } from '@/components/dev-mock-menu/settings';
import { formatDobForApi } from '@/lib/formatters';
import {
  type QueuedInsurance,
  type UnverifiedInsurance,
  type VerifiedInsurance,
} from '@/types/insurance.type.ts';

type MutationParams = {
  patient: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: 'male' | 'female';
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  insurances: QueuedInsurance[];
};

function useInsuranceVerificationMutation(
  options?: UseMutationOptions<
    { verified: VerifiedInsurance[]; unverified: UnverifiedInsurance[] },
    Error,
    MutationParams
  >
) {
  return useMutation({
    mutationFn: async (params) => {
      const debugVerifiedOverride = getDebugVerifiedOverride();
      const body: EligibilityInput = {
        patient: {
          first_name: params.patient.firstName,
          last_name: params.patient.lastName,
          date_of_birth: formatDobForApi(params.patient.dateOfBirth),
          gender: params.patient.gender,
          address_line1: params.patient.addressLine1,
          address_line2: params.patient.addressLine2,
          city: params.patient.city,
          state: params.patient.state,
          zip: params.patient.zipCode,
        },
        insurance: params.insurances.map((insurance) => ({
          carrier_type: insurance.carrier.carrierType,
          uuid: insurance.carrier.uuid,
          member_id: insurance.memberId,
          fingerprint: insurance.carrier.internalId,
          ...(debugVerifiedOverride !== undefined
            ? { debug_verified_override: debugVerifiedOverride }
            : {}),
        })),
      };

      const { data } = await insuranceV3Verification({
        body,
        signal: AbortSignal.timeout(milliseconds({ seconds: 120 })),
      });

      // Temporary compatibility between backend releases: older responses can return
      // base carrier types for managed plans, so keep the FE-selected type from the
      // insurance we matched by fingerprint.
      const normalizedPayload = data.payload.map((apiInsuranceResponseItem) => {
        const matchingInsurance = getMatchingInsurance(params.insurances, apiInsuranceResponseItem);

        if (!matchingInsurance) {
          return apiInsuranceResponseItem;
        }

        return {
          ...apiInsuranceResponseItem,
          carrier_type: matchingInsurance.carrier.carrierType,
        };
      });

      // zip in existing data for requested insurances, (richer than the api response)
      const verified: VerifiedInsurance[] = normalizedPayload
        .filter((item) => item.verified)
        .flatMap((apiInsuranceResponseItem) => {
          const matchingInsurance = getMatchingInsurance(
            params.insurances,
            apiInsuranceResponseItem
          );

          if (matchingInsurance) {
            return [
              {
                carrier: {
                  name: matchingInsurance.carrier.name,
                  state: matchingInsurance.carrier.state,
                  carrierType: matchingInsurance.carrier.carrierType,
                },
                memberId: matchingInsurance.memberId,
                verificationKey: apiInsuranceResponseItem.verification_key,
                resolveSource: 'manual',
              },
            ];
          }

          return [];
        });

      const unverified: UnverifiedInsurance[] = normalizedPayload
        .filter((item) => !item.verified)
        .flatMap((apiInsuranceResponseItem) => {
          const matchingInsurance = getMatchingInsurance(
            params.insurances,
            apiInsuranceResponseItem
          );

          if (matchingInsurance) {
            return [
              {
                ...matchingInsurance,
                verificationKey: apiInsuranceResponseItem.verification_key,
                verifiable: apiInsuranceResponseItem.verifiable,
              },
            ];
          }

          return [];
        });

      return { verified, unverified };
    },
    retry: false,
    ...options,
  });
}

function getMatchingInsurance(
  insurances: QueuedInsurance[],
  apiInsuranceResponseItem: InsuranceEligibilityVerificationResponse['payload'][number]
): QueuedInsurance | undefined {
  return insurances.find(
    (insurance) => insurance.carrier.internalId === apiInsuranceResponseItem.fingerprint
  );
}

export { useInsuranceVerificationMutation };
